import React, { useState, useEffect } from "react";
import Avatar from "@mui/material/Avatar";
import Skeleton from "@mui/material/Skeleton";
import { Business } from "@mui/icons-material";

interface CountryLogoProps {
  alpha2: string;
  size?: number;
  variant?: "circular" | "rounded" | "square";
  sx?: object;
}

export const CountryLogo: React.FC<CountryLogoProps> = ({
  alpha2,
  size = 60,
  variant = "rounded",
  sx = {},
}) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const logoUrl = alpha2
    ? `https://static.hginsights.com/flags/${alpha2.toUpperCase()}.svg`
    : "";

  useEffect(() => {
    if (!alpha2) {
      setLoading(false);
      setError(true);
      return;
    }

    setLoading(true);
    setError(false);

    const img = new Image();
    img.src = logoUrl;

    img.onload = () => {
      setLoading(false);
      setError(false);
    };

    img.onerror = () => {
      setLoading(false);
      setError(true);
    };

    // Cleanup function to prevent state updates on unmounted component
    return () => {
      img.onload = null;
      img.onerror = null;
    };
  }, [alpha2, logoUrl]); // Depend on domain and logoUrl

  // Map Avatar variants to Skeleton variants
  const getSkeletonVariant = () => {
    if (variant === "circular") return "circular";
    return "rectangular"; // Both "rounded" and "square" use rectangular for Skeleton
  };

  if (loading) {
    return (
      <Skeleton
        variant={getSkeletonVariant()}
        width={size}
        height={size}
        sx={{
          ...(variant === "rounded" && { borderRadius: 1 }),
          ...sx,
        }}
      />
    );
  }

  if (error) {
    return (
      <Avatar
        variant={variant}
        sx={{
          width: size,
          height: size,
          backgroundColor: "grey.200",
          color: "grey.500",
          ...sx,
        }}
      >
        <Business />
      </Avatar>
    );
  }

  return (
    <Avatar
      src={logoUrl}
      alt={alpha2}
      variant={variant}
      sx={{
        width: size,
        height: size,
        "& img": {
          objectFit: "contain",
        },
        ...sx,
      }}
    />
  );
};
